import React from "react";
import { Button } from "@/components/ui/button";
import { motion, useReducedMotion } from "framer-motion";
import { Glow } from "@/components/ui/glow";
import { Mockup } from "@/components/ui/mockup";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface SeoHeroProps {
  badge?: React.ReactNode;
  mainTitle: string;
  title: string;
  subtitle: string;
  primaryCta: { label: string; href?: string };
  secondaryCta?: { label: string; href?: string };
  image: {
    src: string;
    alt: string;
  };
}

export function SeoHero({ badge, mainTitle, title, subtitle, primaryCta, secondaryCta, image }: SeoHeroProps) {
  const reduce = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
      },
    },
  };

  return (
    <section className="relative pt-16 sm:pt-20 md:pt-24 pb-16 sm:pb-20 overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
      {/* Background Effects */}
      <Glow variant="above" className="opacity-40" />
      
      {/* Floating Background Elements - Optimized for mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 bg-primary/5 rounded-full blur-2xl sm:blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-24 sm:w-32 md:w-48 h-24 sm:h-32 md:h-48 bg-blue-500/10 rounded-full blur-xl sm:blur-2xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 w-16 sm:w-24 md:w-32 h-16 sm:h-24 md:h-32 bg-purple-500/10 rounded-full blur-lg sm:blur-xl animate-pulse delay-500" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          variants={reduce ? {} : containerVariants}
          initial={reduce ? {} : "hidden"}
          animate="visible"
          className="max-w-7xl mx-auto"
        >
          {/* Badge and Main Title */}
          <div className="text-center mb-12 sm:mb-16">
            {badge && (
              <motion.div variants={reduce ? {} : itemVariants} className="mb-6 sm:mb-8">
                {badge}
              </motion.div>
            )}
            
            <motion.h1 
              variants={reduce ? {} : itemVariants}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4"
            >
              <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                {mainTitle}
              </span>
            </motion.h1>
          </div>

          {/* Split Content */}
          <div className="grid lg:grid-cols-[1fr,1.2fr] gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div variants={reduce ? {} : itemVariants} className="space-y-6 sm:space-y-8 text-center lg:text-left">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-4 sm:mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                  {title}
                </h2>
                <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-2xl mx-auto lg:mx-0">
                  {subtitle}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Button asChild size="lg" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 h-auto min-h-[48px] shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95">
                  <a href={primaryCta.href || "#"}>{primaryCta.label}</a>
                </Button>
                {secondaryCta && (
                  <Button asChild variant="outline" size="lg" className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 h-auto min-h-[48px] shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm active:scale-95">
                    <a href={secondaryCta.href || "#"}>{secondaryCta.label}</a>
                  </Button>
                )}
              </div>

              <p className="text-sm text-muted-foreground">
                No credit card required • Start building today
              </p>
            </motion.div>

            {/* Right Content - Enhanced Image/Video Container */}
            <motion.div 
              variants={reduce ? {} : imageVariants}
              className="relative group order-first lg:order-last"
            >
              {/* Glow Effect Behind Image - Reduced on mobile */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 sm:from-primary/20 to-blue-500/10 sm:to-blue-500/20 rounded-xl sm:rounded-2xl blur-xl sm:blur-2xl scale-110 opacity-30 sm:opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
              
              {/* Main Container */}
              <Mockup className="relative z-10 bg-gradient-to-br from-card via-card to-card/80 backdrop-blur-xl border-border/10 shadow-lg sm:shadow-2xl hover:shadow-primary/20 transition-all duration-500 group-hover:scale-[1.02] active:scale-[0.98]">
                <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-lg sm:rounded-xl">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 600px"
                  />
                  
                  {/* Overlay for Future Video Controls */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </AspectRatio>
              </Mockup>

              {/* Floating Elements - Reduced on mobile */}
              <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 w-12 sm:w-16 md:w-24 h-12 sm:h-16 md:h-24 bg-primary/10 rounded-full blur-lg sm:blur-xl animate-pulse delay-300" />
              <div className="absolute -bottom-2 sm:-bottom-4 -left-2 sm:-left-4 w-16 sm:w-20 md:w-32 h-16 sm:h-20 md:h-32 bg-blue-500/10 rounded-full blur-lg sm:blur-xl animate-pulse delay-700" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}